import { atom } from "jotai";
import { atomFamily } from "jotai/utils";

import { Logger } from "@moment/logging";

import type { SyncAction, Workflow } from "./sync.types";

const logger = new Logger("sync-state.workflow.atom");

// Document-scoped sync action
export const $syncAction = atomFamily((documentId: string | undefined) =>
	atom(undefined as SyncAction | undefined)
);

// Dedicated setter for sync action with logging
export const $setSyncAction = atom(
	null,
	(
		_get,
		set,
		params: {
			documentId: string;
			action: SyncAction | undefined;
			workflow: string;
		}
	) => {
		logger.debug(`Starting sync action: ${params.action} in workflow: ${params.workflow}`);
		set($syncAction(params.documentId), params.action);
	}
);
// Alias for backward compatibility
export const $syncActionWithLogging = $setSyncAction;

// Document-scoped syncing state
export const $isSyncing = atomFamily((documentId: string | undefined) => atom(false as boolean));

// Document-scoped workflow
export const $currentWorkflowRunning = atomFamily((documentId: string | undefined) =>
	atom(undefined as Workflow | undefined)
);

// Document-scoped error
export const $syncError = atomFamily((documentId: string | undefined) =>
	atom(null as Error | null)
);

// Document-scoped plans - simple atomFamily for reading
export const $publishPlan = atomFamily((documentId: string | undefined) => {
	const planAtom = atom<SyncAction[]>([]);
	planAtom.debugLabel = `publishPlan/${documentId}`;
	return planAtom;
});

export const $sharePlan = atomFamily((documentId: string | undefined) => {
	const planAtom = atom<SyncAction[]>([]);
	planAtom.debugLabel = `sharePlan/${documentId}`;
	return planAtom;
});

// Dedicated setter atoms for plans
export const $setPublishPlan = atom(
	null,
	(_get, set, params: { documentId: string; plan: SyncAction[] }) => {
		set($publishPlan(params.documentId), params.plan);
	}
);

export const $appendToPublishPlan = atom(
	null,
	(get, set, params: { documentId: string; action: SyncAction }) => {
		const current = get($publishPlan(params.documentId));
		set($publishPlan(params.documentId), [...current, params.action]);
	}
);

export const $setSharePlan = atom(
	null,
	(_get, set, params: { documentId: string; plan: SyncAction[] }) => {
		set($sharePlan(params.documentId), params.plan);
	}
);

export const $appendToSharePlan = atom(
	null,
	(get, set, params: { documentId: string; action: SyncAction }) => {
		const current = get($sharePlan(params.documentId));
		set($sharePlan(params.documentId), [...current, params.action]);
	}
);

// Whether to skip collab initialization when sharing (controlled from UI toggle)
// Document-scoped to prevent stale state across navigations
export const $skipCollab = atomFamily((documentId: string | undefined) => {
	const skipCollabAtom = atom(false as boolean);
	skipCollabAtom.debugLabel = `skipCollab/${documentId}`;
	return skipCollabAtom;
});

// Document-scoped sync state (combines isSyncing and workflow)
export const $syncState = atomFamily((documentId: string | undefined) =>
	atom(
		(get) => {
			return {
				isSyncing: get($isSyncing(documentId)),
				currentWorkflow: get($currentWorkflowRunning(documentId)),
			};
		},
		(
			_get,
			set,
			params: {
				isSyncing: boolean;
				workflow?: Workflow | undefined;
			}
		) => {
			set($isSyncing(documentId), params.isSyncing);
			set($currentWorkflowRunning(documentId), params.workflow);
		}
	)
);
